import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "./session.js";
import type {
  AllocationPolicyDto,
  AllocationSettingsResponse,
  AuditHistoryResponse,
  AwsConnectionsResponse,
  ExplorerQueryResponse,
  ExplorerQuerySpec,
  IngestionObservabilityResponse,
  MembersResponse,
  ReportDocument,
  ReportExportResponse,
  ReportId,
  ReportsListResponse,
  SavedViewDto,
  SavedViewsListResponse,
  SettingsOverviewResponse,
  EntitlementsDto,
  CommandCenterResponse,
  SavingsRadarResponse,
  BillingOverviewResponse,
  NotificationDto,
  AlertPreferencesDto,
  ServiceRequestDto,
} from "./client.js";
import type {
  AssessmentResponse,
  DecommissionDetailResponse,
  DecommissionListResponse,
  DualRunListResponse,
  DualRunResponse,
  BudgetsResponse,
  CommitmentsResponse,
  EconomicsResponse,
  FindingDetailResponse,
  FindingsListResponse,
  GovernanceOverviewResponse,
  GovernancePoliciesResponse,
  MetadataCompletenessResponse,
  ArchitectureTreeResponse,
  ArchNodeDetailResponse,
  ArchNodeType,
  ArchResourceMappingResponse,
  ArchResourceTableResponse,
  OverviewKpis,
  RecommendationDetailResponse,
  RecommendationsListResponse,
  ValueLedgerDetailResponse,
  ValueLedgerListResponse,
  PortfolioResponse,
  ReadinessResponse,
  WavesResponse,
} from "./client.js";

/**
 * Typed data hooks (TanStack Query). Each is scoped to the active tenant so
 * switching tenant re-fetches. The queryKey includes the tenant id so caches
 * never bleed across tenants in the client.
 */

export function useOverview(): UseQueryResult<OverviewKpis> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["overview", session.tenantId],
    queryFn: () => client.get<OverviewKpis>("/overview"),
  });
}

export function usePortfolio(params: { groupBy?: string | undefined; strategy?: string | undefined; search?: string | undefined }): UseQueryResult<PortfolioResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["portfolio", session.tenantId, params],
    queryFn: () =>
      client.get<PortfolioResponse>("/portfolio", {
        groupBy: params.groupBy,
        strategy: params.strategy,
        search: params.search,
      }),
  });
}

export function useEconomics(workloadId: string | null): UseQueryResult<EconomicsResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["economics", session.tenantId, workloadId],
    enabled: workloadId != null,
    queryFn: () => client.get<EconomicsResponse>(`/workloads/${workloadId}/economics`),
  });
}

export function useAssessment(workloadId: string | null): UseQueryResult<AssessmentResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["assessment", session.tenantId, workloadId],
    enabled: workloadId != null,
    queryFn: () => client.get<AssessmentResponse>(`/workloads/${workloadId}/assessment`),
  });
}

export function useReadiness(workloadId: string | null): UseQueryResult<ReadinessResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["readiness", session.tenantId, workloadId],
    enabled: workloadId != null,
    queryFn: () => client.get<ReadinessResponse>(`/workloads/${workloadId}/readiness`),
  });
}

export function useWaves(): UseQueryResult<WavesResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["waves", session.tenantId],
    queryFn: () => client.get<WavesResponse>("/waves"),
  });
}

export function useDualRunList(): UseQueryResult<DualRunListResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["dual-run-list", session.tenantId],
    queryFn: () => client.get<DualRunListResponse>("/dual-run"),
  });
}

export function useDualRun(workloadId: string | null): UseQueryResult<DualRunResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["dual-run", session.tenantId, workloadId],
    enabled: workloadId != null,
    queryFn: () => client.get<DualRunResponse>(`/workloads/${workloadId}/dual-run`),
  });
}

export function useDecommissionList(): UseQueryResult<DecommissionListResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["decommission-list", session.tenantId],
    queryFn: () => client.get<DecommissionListResponse>("/decommission"),
  });
}

export function useDecommission(workloadId: string | null): UseQueryResult<DecommissionDetailResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["decommission", session.tenantId, workloadId],
    enabled: workloadId != null,
    queryFn: () => client.get<DecommissionDetailResponse>(`/workloads/${workloadId}/decommission`),
  });
}

export function useFindings(params: { status?: string | undefined }): UseQueryResult<FindingsListResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["findings", session.tenantId, params],
    queryFn: () => client.get<FindingsListResponse>("/findings", { status: params.status }),
  });
}

export function useFinding(findingId: string | null): UseQueryResult<FindingDetailResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["finding", session.tenantId, findingId],
    enabled: findingId != null,
    queryFn: () => client.get<FindingDetailResponse>(`/findings/${findingId}`),
  });
}

export function useRecommendations(params: { status?: string | undefined; category?: string | undefined }): UseQueryResult<RecommendationsListResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["recommendations", session.tenantId, params],
    queryFn: () => client.get<RecommendationsListResponse>("/recommendations", { status: params.status, category: params.category }),
  });
}

export function useRecommendation(recommendationId: string | null): UseQueryResult<RecommendationDetailResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["recommendation", session.tenantId, recommendationId],
    enabled: recommendationId != null,
    queryFn: () => client.get<RecommendationDetailResponse>(`/recommendations/${recommendationId}`),
  });
}

export function useCommitments(): UseQueryResult<CommitmentsResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["commitments", session.tenantId],
    queryFn: () => client.get<CommitmentsResponse>("/commitments"),
  });
}

export function useGovernanceOverview(): UseQueryResult<GovernanceOverviewResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["governance-overview", session.tenantId],
    queryFn: () => client.get<GovernanceOverviewResponse>("/governance/overview"),
  });
}

export function useMetadataCompleteness(): UseQueryResult<MetadataCompletenessResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["governance-metadata", session.tenantId],
    queryFn: () => client.get<MetadataCompletenessResponse>("/governance/metadata"),
  });
}

export function useBudgets(): UseQueryResult<BudgetsResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["governance-budgets", session.tenantId],
    queryFn: () => client.get<BudgetsResponse>("/governance/budgets"),
  });
}

export function useGovernancePolicies(params: { status?: string | undefined }): UseQueryResult<GovernancePoliciesResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["governance-policies", session.tenantId, params],
    queryFn: () => client.get<GovernancePoliciesResponse>("/governance/policies", { status: params.status }),
  });
}

export function useValueLedger(params: { status?: string | undefined }): UseQueryResult<ValueLedgerListResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["value-ledger", session.tenantId, params],
    queryFn: () => client.get<ValueLedgerListResponse>("/value-ledger", { status: params.status }),
  });
}

export function useValueLedgerEntry(ledgerEntryId: string | null): UseQueryResult<ValueLedgerDetailResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["value-ledger-entry", session.tenantId, ledgerEntryId],
    enabled: ledgerEntryId != null,
    queryFn: () => client.get<ValueLedgerDetailResponse>(`/value-ledger/${ledgerEntryId}`),
  });
}

export function useArchitectureTree(parent: { type: ArchNodeType; id: string } | null): UseQueryResult<ArchitectureTreeResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["arch-tree", session.tenantId, parent?.type ?? "root", parent?.id ?? ""],
    queryFn: () =>
      client.get<ArchitectureTreeResponse>("/architecture/tree", parent ? { parentType: parent.type, parentId: parent.id } : {}),
  });
}

export function useArchitectureNode(node: { type: ArchNodeType; id: string } | null): UseQueryResult<ArchNodeDetailResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["arch-node", session.tenantId, node?.type ?? "", node?.id ?? ""],
    enabled: node != null,
    queryFn: () => client.get<ArchNodeDetailResponse>(`/architecture/node/${node!.type}/${node!.id}`),
  });
}

export function useArchitectureResources(
  node: { type: ArchNodeType; id: string } | null,
  page: { limit: number; offset: number }
): UseQueryResult<ArchResourceTableResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["arch-resources", session.tenantId, node?.type ?? "", node?.id ?? "", page.limit, page.offset],
    enabled: node != null,
    queryFn: () =>
      client.get<ArchResourceTableResponse>("/architecture/resources", {
        type: node!.type,
        id: node!.id,
        limit: String(page.limit),
        offset: String(page.offset),
      }),
  });
}

export function useResourceMapping(resourceId: string | null): UseQueryResult<ArchResourceMappingResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["arch-resource-mapping", session.tenantId, resourceId],
    enabled: resourceId != null,
    queryFn: () => client.get<ArchResourceMappingResponse>(`/architecture/resources/${resourceId}/mapping`),
  });
}

// --- Cost Explorer ----------------------------------------------------------

/**
 * Run an Explorer query. The spec is sent to the server which does ALL the
 * aggregation (per-currency SUM/GROUP BY over the authoritative facts). The
 * query is disabled until `enabled` so the screen controls when it fires.
 */
export function useExplorerQuery(spec: ExplorerQuerySpec | null, enabled: boolean): UseQueryResult<ExplorerQueryResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["explorer-query", session.tenantId, spec],
    enabled: enabled && spec != null,
    queryFn: () => client.post<ExplorerQueryResponse>("/explorer/query", spec),
  });
}

export function useExplorerViews(): UseQueryResult<SavedViewsListResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["explorer-views", session.tenantId],
    queryFn: () => client.get<SavedViewsListResponse>("/explorer/views"),
  });
}

export function useCreateExplorerView(): UseMutationResult<SavedViewDto, Error, { name: string; spec: ExplorerQuerySpec; shared: boolean }> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => client.post<SavedViewDto>("/explorer/views", body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["explorer-views", session.tenantId] }),
  });
}

export function useUpdateExplorerView(): UseMutationResult<SavedViewDto, Error, { viewId: string; name?: string; spec?: ExplorerQuerySpec; shared?: boolean }> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ viewId, ...body }) => client.put<SavedViewDto>(`/explorer/views/${viewId}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["explorer-views", session.tenantId] }),
  });
}

export function useDeleteExplorerView(): UseMutationResult<{ deleted: boolean }, Error, string> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewId) => client.delete<{ deleted: boolean }>(`/explorer/views/${viewId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["explorer-views", session.tenantId] }),
  });
}

// --- Reports ----------------------------------------------------------------

export function useReportsList(): UseQueryResult<ReportsListResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["reports", session.tenantId],
    queryFn: () => client.get<ReportsListResponse>("/reports"),
  });
}

export function useReportDocument(reportId: ReportId | null): UseQueryResult<ReportDocument> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["report", session.tenantId, reportId],
    enabled: reportId != null,
    queryFn: () => client.get<ReportDocument>(`/reports/${reportId}`),
  });
}

/** Fetch a server-generated export (base64) and trigger a browser download. */
export function useReportExport(): UseMutationResult<ReportExportResponse, Error, { reportId: ReportId; format: "csv" | "xlsx" }> {
  const { client } = useSession();
  return useMutation({
    mutationFn: ({ reportId, format }) => client.get<ReportExportResponse>(`/reports/${reportId}/export`, { format }),
    onSuccess: (res) => {
      // Decode base64 to a Blob and download. The browser never reconstructs the
      // authoritative money; it only saves the bytes the server produced.
      const bin = atob(res.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: res.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}

// --- Settings / Admin -------------------------------------------------------

export function useSettingsOverview(): UseQueryResult<SettingsOverviewResponse> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["settings", session.tenantId], queryFn: () => client.get<SettingsOverviewResponse>("/settings") });
}

export function useSettingsMembers(): UseQueryResult<MembersResponse> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["settings-members", session.tenantId], queryFn: () => client.get<MembersResponse>("/settings/members") });
}

export function useAwsConnections(): UseQueryResult<AwsConnectionsResponse> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["settings-aws", session.tenantId], queryFn: () => client.get<AwsConnectionsResponse>("/settings/aws-connections") });
}

export function useAllocationSettings(): UseQueryResult<AllocationSettingsResponse> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["settings-allocation", session.tenantId], queryFn: () => client.get<AllocationSettingsResponse>("/settings/allocation") });
}

export function useAuditHistory(): UseQueryResult<AuditHistoryResponse> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["settings-audit", session.tenantId], queryFn: () => client.get<AuditHistoryResponse>("/settings/audit") });
}

export function useGrantRole(): UseMutationResult<{ granted: boolean }, Error, { userSub: string; role: string }> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => client.post<{ granted: boolean }>("/settings/members/roles", body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["settings-members", session.tenantId] }),
  });
}

export function useRevokeRole(): UseMutationResult<{ revoked: boolean }, Error, { userSub: string; role: string }> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userSub, role }) => client.delete<{ revoked: boolean }>(`/settings/members/${userSub}/roles/${role}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["settings-members", session.tenantId] }),
  });
}

export function useRemoveMember(): UseMutationResult<{ removedRoles: number }, Error, string> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userSub) => client.delete<{ removedRoles: number }>(`/settings/members/${userSub}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["settings-members", session.tenantId] }),
  });
}

export function useUpdateAllocationSettings(): UseMutationResult<AllocationPolicyDto, Error, Partial<AllocationPolicyDto>> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => client.put<AllocationPolicyDto>("/settings/allocation", body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["settings-allocation", session.tenantId] }),
  });
}

export function useIngestionObservability(): UseQueryResult<IngestionObservabilityResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["observability-ingestion", session.tenantId],
    queryFn: () => client.get<IngestionObservabilityResponse>("/observability/ingestion"),
  });
}

// ---- Commercial SaaS ---------------------------------------------------------

export function useEntitlements(): UseQueryResult<EntitlementsDto> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["entitlements", session.tenantId], queryFn: () => client.get<EntitlementsDto>("/entitlements"), staleTime: 60_000 });
}

export function useCommandCenter(): UseQueryResult<CommandCenterResponse> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["command-center", session.tenantId], queryFn: () => client.get<CommandCenterResponse>("/command-center") });
}

export function useSavingsRadar(): UseQueryResult<SavingsRadarResponse> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["savings-radar", session.tenantId], queryFn: () => client.get<SavingsRadarResponse>("/savings-radar") });
}

export function useBilling(): UseQueryResult<BillingOverviewResponse> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["billing", session.tenantId],
    queryFn: () => client.get<BillingOverviewResponse>("/billing"),
    // A Marketplace purchase waiting for AWS to confirm the licence activates by itself: keep looking.
    refetchInterval: (query) => (query.state.data?.subscription.marketplacePending ? 10_000 : false),
  });
}

/** Starts hosted checkout / the billing portal and leaves the app for the provider page. */
export function useBillingRedirect(): UseMutationResult<{ url: string }, Error, { kind: "checkout"; planCode: string } | { kind: "portal" }> {
  const { client } = useSession();
  return useMutation({
    mutationFn: (v) => (v.kind === "checkout" ? client.post<{ url: string }>("/billing/checkout", { planCode: v.planCode }) : client.post<{ url: string }>("/billing/portal", {})),
    onSuccess: (r) => {
      window.location.assign(r.url);
    },
  });
}

export function useNotifications(): UseQueryResult<{ notifications: NotificationDto[]; unread: number }> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["notifications", session.tenantId],
    queryFn: () => client.get<{ notifications: NotificationDto[]; unread: number }>("/notifications"),
    refetchInterval: 5 * 60_000,
  });
}

export function useMarkNotificationRead(): UseMutationResult<{ updated: number }, Error, string> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => client.post<{ updated: number }>(`/notifications/${encodeURIComponent(id)}/read`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications", session.tenantId] });
      void qc.invalidateQueries({ queryKey: ["command-center", session.tenantId] });
    },
  });
}

export function useAlertPreferences(): UseQueryResult<AlertPreferencesDto> {
  const { client, session } = useSession();
  return useQuery({ queryKey: ["alert-prefs", session.tenantId], queryFn: () => client.get<AlertPreferencesDto>("/settings/alerts") });
}

export function useUpdateAlertPreferences(): UseMutationResult<AlertPreferencesDto, Error, Partial<AlertPreferencesDto>> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v) => client.put<AlertPreferencesDto>("/settings/alerts", v),
    onSuccess: (d) => qc.setQueryData(["alert-prefs", session.tenantId], d),
  });
}

export function useServiceRequests(): UseQueryResult<{ requests: ServiceRequestDto[]; consentTextVersion: string }> {
  const { client, session } = useSession();
  return useQuery({
    queryKey: ["service-requests", session.tenantId],
    queryFn: () => client.get<{ requests: ServiceRequestDto[]; consentTextVersion: string }>("/service-requests"),
  });
}

export function useCreateServiceRequest(): UseMutationResult<
  ServiceRequestDto,
  Error,
  { topic: string; description: string; contactEmail?: string; consentContact: boolean; consentShareData: boolean }
> {
  const { client, session } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v) => client.post<ServiceRequestDto>("/service-requests", v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["service-requests", session.tenantId] }),
  });
}
